function normalizeText(value) {
	if (value === undefined || value === null) {
		return "";
	}
	return String(value).trim();
}

/**
 * Resuming arbitrary organization history opens the underlying activity in the
 * editable Player, so this permission must remain narrower than Status-board
 * permissions such as viewing jobs or changing workflow.
 */
export function canResumeAnyHistoryInCurrentOrg({
	isOrganizationAdministrator = false
} = {}) {
	return isOrganizationAdministrator === true;
}

function normalizeResumeSession(record, activityId, instanceId, overrideUrl = "") {
	if (!record || typeof record !== "object") {
		return null;
	}

	const requestedActivityId = normalizeText(activityId);
	const requestedInstanceId = normalizeText(instanceId);
	const recordActivityId = normalizeText(record.id || record.activityId);
	const recordInstanceId = normalizeText(record.instanceId || record.objectID);
	const url = normalizeText(overrideUrl || record.url || record.instanceUrl);

	if (!requestedActivityId || !requestedInstanceId ||
		recordActivityId !== requestedActivityId ||
		recordInstanceId !== requestedInstanceId ||
		!url) {
		return null;
	}

	return {
		...record,
		id: requestedActivityId,
		instanceId: requestedInstanceId,
		objectID: normalizeText(record.objectID) || requestedInstanceId,
		url
	};
}

export function findExactResumeSession(sessions, activityId, instanceId) {
	const candidates = Array.isArray(sessions) ? sessions : [];
	for (let index = 0; index < candidates.length; index++) {
		const session = normalizeResumeSession(candidates[index], activityId, instanceId);
		if (session) {
			return session;
		}
	}
	return null;
}

export function buildHistoryResumeFilters({
	instanceId,
	identityId,
	unrestricted = false
} = {}) {
	const requestedInstanceId = normalizeText(instanceId);
	const requestedIdentityId = normalizeText(identityId);

	if (!requestedInstanceId || (!unrestricted && !requestedIdentityId)) {
		return null;
	}

	const filters = [`objectID:${JSON.stringify(requestedInstanceId)}`];
	if (!unrestricted) {
		filters.push(`identityId:${JSON.stringify(requestedIdentityId)}`);
	}

	return filters.join(" AND ");
}

/**
 * Resolve an existing activity instance without ever treating a missing cached
 * session as permission to create a new object under the requested instance ID.
 */
export async function resolveActivityResume({
	activityId,
	instanceId,
	providedUrl = "",
	sessions = [],
	loadHistorySession
} = {}) {
	const requestedActivityId = normalizeText(activityId);
	const requestedInstanceId = normalizeText(instanceId);
	const requestedUrl = normalizeText(providedUrl);
	const cachedSession = findExactResumeSession(sessions, requestedActivityId, requestedInstanceId);

	// The cached session is already part of the caller's authorized session set
	// and contains the canonical object key. A stale URL supplied by a link or
	// caller must not override it.
	if (cachedSession) {
		return {
			status: "resolved",
			source: "sessionCache",
			url: cachedSession.url,
			session: cachedSession
		};
	}

	if (requestedUrl && requestedActivityId && requestedInstanceId) {
		return {
			status: "resolved",
			source: "providedUrl",
			url: requestedUrl,
			session: normalizeResumeSession(
				{
					id: requestedActivityId,
					instanceId: requestedInstanceId,
					objectID: requestedInstanceId
				},
				requestedActivityId,
				requestedInstanceId,
				requestedUrl
			)
		};
	}

	if (!requestedInstanceId) {
		return {
			status: "startNew",
			source: "missingInstanceId"
		};
	}

	if (typeof loadHistorySession !== "function") {
		return {
			status: "blocked",
			source: "historyLookupUnavailable"
		};
	}

	try {
		const historyRecord = await loadHistorySession(requestedInstanceId);
		if (!historyRecord) {
			return {
				status: "blocked",
				source: "historyRecordMissing"
			};
		}
		const historySession = normalizeResumeSession(
			historyRecord,
			requestedActivityId,
			requestedInstanceId
		);
		if (!historySession) {
			return {
				status: "blocked",
				source: "historyRecordInvalid"
			};
		}

		return {
			status: "resolved",
			source: "historyIndex",
			url: historySession.url,
			session: historySession
		};
	} catch (error) {
		return {
			status: "blocked",
			source: "historyLookupFailed",
			error
		};
	}
}
